import React from 'react';
import { cn } from '../lib/cn';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** Visual style variant */
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
    /** Size of the button */
    size?: 'sm' | 'md' | 'lg';
    /** Show loading spinner */
    isLoading?: boolean;
    /** Full width button */
    fullWidth?: boolean;
}

const variantStyles = {
    // Primary: Gold background, dark text (BOBY brand standard)
    primary: 'bg-primary text-text-primary hover:bg-primary-dark focus:ring-primary/50 font-semibold',
    // Secondary: Grey background
    secondary: 'bg-grey-200 text-text-primary hover:bg-grey-300 focus:ring-grey-300/50',
    // Outline: Gold border
    outline: 'border-2 border-primary text-text-primary hover:bg-primary/10 focus:ring-primary/50',
    // Ghost: Text only
    ghost: 'text-text-secondary hover:bg-grey-100 hover:text-text-primary focus:ring-grey-300/50',
    // Danger: Red for destructive actions
    danger: 'bg-danger text-white hover:bg-red-700 focus:ring-danger/50',
};

const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
};

/**
 * Primary button component for the Boby design system
 * Uses GOLD (#FFD952) as primary brand color
 * NO EMOJI ICONS - text only
 * 
 * @example
 * <Button variant="primary" onClick={() => {}}>
 *   Save Changes
 * </Button>
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant = 'primary',
            size = 'md',
            isLoading = false,
            fullWidth = false,
            disabled,
            children,
            ...props
        },
        ref
    ) => {
        return (
            <button
                ref={ref}
                disabled={disabled || isLoading}
                className={cn(
                    // Base styles
                    'inline-flex items-center justify-center font-medium rounded-lg',
                    'transition-all duration-200 ease-in-out',
                    'focus:outline-none focus:ring-2 focus:ring-offset-2',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    // Variant styles
                    variantStyles[variant],
                    // Size styles
                    sizeStyles[size],
                    // Full width
                    fullWidth && 'w-full',
                    className
                )}
                {...props}
            >
                {isLoading && (
                    <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                    </svg>
                )}
                {children}
            </button>
        );
    }
);

Button.displayName = 'Button';
